import React, { memo, useState } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { kebabCase } from 'lodash';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as zod from 'zod';
import {
  AiAgentsNamespaceCreateWebsiteSourceArgs,
  AiAgentsNamespaceUpdateWebsiteSourceArgs,
  AiAgentWebsiteCrawlMode,
  AiAgentWebsiteSitemapMode,
  Mutation,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { Icon } from 'src/components/general/icon';
import { LggToggleSwitch } from 'src/components/general/inputs/lgg-toggle-switch';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { TextInput } from 'src/components/general/inputs/text-input';
import { InlineFormModal } from 'src/components/general/modals/inline-form/inline-form-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  CREATE_WEBSITE_SOURCE_MUTATION,
  UPDATE_WEBSITE_SOURCE_MUTATION,
} from 'src/components/pages/ai-agents/knowledge-base-sources/websites/queries';
import type { WebsiteFormTarget } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/website-sources';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useVisible } from 'src/hooks/use-visible';

const DEFAULT_MAX_PAGES = 30;
const DEFAULT_CRAWL_DEPTH = 3;
const DEFAULT_SITEMAP: AiAgentWebsiteSitemapMode = 'INCLUDE';

const CRAWL_MODES = ['SINGLE_PAGE', 'WEBSITE'] as const;

const MODE_LABEL_KEYS: Record<AiAgentWebsiteCrawlMode, 'singlePage' | 'multiPage'> = {
  SINGLE_PAGE: 'singlePage',
  WEBSITE: 'multiPage',
};

const SITEMAP_MODES = ['INCLUDE', 'SKIP', 'ONLY'] as const;

const httpUrl = zod
  .string()
  .trim()
  .refine((value) => {
    try {
      const { protocol } = new URL(value);

      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  });

const websiteFormSchema = zod
  .object({
    mode: zod.enum(CRAWL_MODES),
    url: httpUrl,
    maxPages: zod.coerce.number(),
    crawlDepth: zod.coerce.number(),
    sitemap: zod.enum(SITEMAP_MODES),
    includePaths: zod.array(zod.string()),
    excludePaths: zod.array(zod.string()),
    allowSubdomains: zod.boolean(),
  })
  .superRefine((values, context) => {
    if (values.mode !== 'WEBSITE') {
      return;
    }

    (['maxPages', 'crawlDepth'] as const).forEach((name) => {
      if (!Number.isInteger(values[name]) || values[name] < 1) {
        context.addIssue({ code: 'custom', path: [name], message: name });
      }
    });
  });

type WebsiteFormValues = zod.infer<typeof websiteFormSchema>;

const ADDED_DEFAULTS: WebsiteFormValues = {
  mode: 'SINGLE_PAGE',
  url: '',
  maxPages: DEFAULT_MAX_PAGES,
  crawlDepth: DEFAULT_CRAWL_DEPTH,
  sitemap: DEFAULT_SITEMAP,
  includePaths: [],
  excludePaths: [],
  allowSubdomains: false,
};

const toFormValues = (target: WebsiteFormTarget): WebsiteFormValues => {
  if (target.mode === 'add') {
    return ADDED_DEFAULTS;
  }

  const { website } = target;

  return {
    mode: website.mode,
    url: website.url,
    maxPages: website.maxPages ?? DEFAULT_MAX_PAGES,
    crawlDepth: website.crawlDepth ?? DEFAULT_CRAWL_DEPTH,
    sitemap: website.sitemap ?? DEFAULT_SITEMAP,
    includePaths: website.includePaths,
    excludePaths: website.excludePaths,
    allowSubdomains: website.allowSubdomains,
  };
};

const hasAdvancedSettings = (values: WebsiteFormValues) =>
  values.sitemap !== DEFAULT_SITEMAP ||
  values.crawlDepth !== DEFAULT_CRAWL_DEPTH ||
  values.includePaths.length > 0 ||
  values.excludePaths.length > 0 ||
  values.allowSubdomains;

const toSettings = (values: WebsiteFormValues) => {
  if (values.mode === 'SINGLE_PAGE') {
    return { mode: values.mode, url: values.url.trim() };
  }

  return {
    mode: values.mode,
    url: values.url.trim(),
    maxPages: values.maxPages,
    crawlDepth: values.crawlDepth,
    sitemap: values.sitemap,
    includePaths: values.includePaths.map((path) => path.trim()).filter(Boolean),
    excludePaths: values.excludePaths.map((path) => path.trim()).filter(Boolean),
    allowSubdomains: values.allowSubdomains,
  };
};

const Fields = styled(FlexColumn)<{ $singlePage: boolean }>`
  gap: 15px;
  padding: 10px 0;

  ${up('md')} {
    min-height: ${({ $singlePage }) => ($singlePage ? '252px' : 'unset')};
  }
`;

const LabeledField = styled(FlexColumn)`
  gap: 1px;
`;

const ModeSwitch = styled(FlexRow)`
  border: 1px solid ${({ theme }) => theme.colors.koala};
  border-radius: 4px;
  height: 34px;
  overflow: hidden;
`;

const ModeOption = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.white : theme.colors.porcelain};
  border: none;
  color: ${({ $active, theme }) => ($active ? theme.colors.gogo : theme.colors.flint)};
  cursor: pointer;
  flex: 1;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 18px;
  padding: 0 15px;

  &:disabled {
    color: ${({ theme }) => theme.colors.flint};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LockedUrlField = styled.div`
  input:disabled {
    background: ${({ theme }) => theme.colors.porcelain};
    color: ${({ theme }) => theme.colors.flint};
    cursor: not-allowed;
  }
`;

const FieldLabel = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 19.2px;
`;

const LinkButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gogo};
  cursor: pointer;
  display: flex;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  gap: 5px;
  line-height: 18px;
  padding: 0;
`;

const AddPathButton = styled(LinkButton)`
  font-size: 12px;
  gap: 6px;
  line-height: 15px;
`;

const LinkIcon = styled(Icon)<{ $size: number; $flipped?: boolean }>`
  svg {
    height: ${({ $size }) => $size}px;
    transform: ${({ $flipped }) => ($flipped ? 'scaleY(-1)' : 'none')};
    width: ${({ $size }) => $size}px;

    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const RemovePathIcon = styled(Icon)`
  cursor: pointer;

  svg {
    height: 18px;
    width: 18px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const PathRow = styled(FlexRow)`
  align-items: center;
  gap: 8px;

  > :first-child {
    flex: 1;
  }
`;

const PathList = styled(FlexColumn)<{ $hasPaths: boolean }>`
  gap: ${({ $hasPaths }) => ($hasPaths ? '12px' : '8px')};
`;

const PathRows = styled(FlexColumn)`
  gap: 8px;
`;

const SuffixedInput = styled.div`
  position: relative;
`;

const InputSuffix = styled.span`
  bottom: 11px;
  color: ${({ theme }) => theme.colors.flint};
  font-size: 13px;
  line-height: 16px;
  pointer-events: none;
  position: absolute;
  right: 11px;
`;

const SwitchRow = styled(FlexRow)`
  align-items: center;
  gap: 20px;
  padding-top: 10px;
`;

const SwitchText = styled(FlexColumn)`
  flex: 1;
`;

const HelpText = styled.span`
  color: ${({ theme }) => theme.colors.steel};
  font-size: 12px;
`;

type PathsFieldProps = {
  control: Control<WebsiteFormValues>;
  name: 'includePaths' | 'excludePaths';
};

const PathsField = memo<PathsFieldProps>(({ control, name }) => {
  const { t } = useTranslation(['aiAgents']);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value: paths, onChange } }) => (
        <PathList $hasPaths={paths.length > 0}>
          <PathRows>
            <FieldLabel>{t(`aiAgents:knowledge.websites.form.${name}`)}</FieldLabel>
            {paths.map((path, index) => (
              <PathRow key={index}>
                <TextInput
                  data-lgg-id={`website-form-${kebabCase(name)}-input`}
                  value={path}
                  reserveErrorArea={false}
                  onChange={(event) =>
                    onChange(
                      paths.map((current, i) =>
                        i === index ? event.target.value : current,
                      ),
                    )
                  }
                />
                <RemovePathIcon
                  type="remove"
                  lggTestId={`website-form-${kebabCase(name)}-remove`}
                  onClick={() => onChange(paths.filter((_, i) => i !== index))}
                />
              </PathRow>
            ))}
          </PathRows>
          <AddPathButton
            type="button"
            data-lgg-id={`website-form-${kebabCase(name)}-add`}
            onClick={() => onChange([...paths, ''])}
          >
            <LinkIcon type="plusThick" $size={10} />
            {t('aiAgents:knowledge.websites.form.addPath')}
          </AddPathButton>
        </PathList>
      )}
    />
  );
});

const AdvancedSettings = memo<{ control: Control<WebsiteFormValues> }>(({ control }) => {
  const { t } = useTranslation(['aiAgents']);
  const sitemapOptions: SelectOption<AiAgentWebsiteSitemapMode>[] = SITEMAP_MODES.map(
    (value) => ({
      value,
      label: t(`aiAgents:knowledge.websites.form.sitemapOptions.${value}`),
    }),
  );

  return (
    <>
      <Controller
        control={control}
        name="sitemap"
        render={({ field }) => (
          <LabeledField>
            <FieldLabel>{t('aiAgents:knowledge.websites.form.sitemap')}</FieldLabel>
            <Select<SelectOption<AiAgentWebsiteSitemapMode>>
              name="website-form-sitemap"
              isSearchable={false}
              isClearable={false}
              forceCaret
              options={sitemapOptions}
              value={sitemapOptions.filter(({ value }) => value === field.value)}
              onChange={(option) => field.onChange(option?.value ?? DEFAULT_SITEMAP)}
            />
          </LabeledField>
        )}
      />
      <Controller
        control={control}
        name="crawlDepth"
        render={({ field, fieldState }) => (
          <LabeledField>
            <FieldLabel>{t('aiAgents:knowledge.websites.form.crawlDepth')}</FieldLabel>
            <TextInput
              {...field}
              type="number"
              min={1}
              data-lgg-id="website-form-crawl-depth"
              error={Boolean(fieldState.error)}
              showErrorMessage={false}
              reserveErrorArea={false}
            />
          </LabeledField>
        )}
      />
      <PathsField control={control} name="includePaths" />
      <PathsField control={control} name="excludePaths" />
      <Controller
        control={control}
        name="allowSubdomains"
        render={({ field }) => (
          <SwitchRow>
            <SwitchText>
              <FieldLabel>
                {t('aiAgents:knowledge.websites.form.includeSubdomains')}
              </FieldLabel>
              <HelpText>
                {t('aiAgents:knowledge.websites.form.includeSubdomainsHelp')}
              </HelpText>
            </SwitchText>
            <LggToggleSwitch
              data-lgg-id="website-form-allow-subdomains"
              checked={field.value}
              onChange={field.onChange}
            />
          </SwitchRow>
        )}
      />
    </>
  );
});

type WebsiteFormModalProps = {
  knowledgeBaseId: string;
  target: WebsiteFormTarget;
  onClose: VoidFunction;
  onSaved: VoidFunction;
};

export const WebsiteFormModal = memo<WebsiteFormModalProps>(
  ({ knowledgeBaseId, target, onClose, onSaved }) => {
    const { t } = useTranslation(['aiAgents', 'common']);
    const { id: companyId } = useCurrentInstitution();
    const handleGraphQLError = useHandleGraphQLError();
    const visibilityHandler = useVisible(true);
    const defaultValues = toFormValues(target);
    const [showAdvanced, setShowAdvanced] = useState(hasAdvancedSettings(defaultValues));
    const form = useForm<WebsiteFormValues>({
      resolver: zodResolver(websiteFormSchema),
      defaultValues,
      mode: 'onChange',
    });
    const isMultiPage = form.watch('mode') === 'WEBSITE';
    const isEdit = target.mode === 'edit';
    const title = isEdit
      ? t('aiAgents:knowledge.websites.form.editTitle')
      : t('aiAgents:knowledge.websites.form.addTitle');
    const submitButtonText = isEdit
      ? t('common:save')
      : t('aiAgents:knowledge.websites.form.add');

    const [createWebsite, { loading: creating }] = useMutation<
      Pick<Mutation, 'aiAgents'>,
      AiAgentsNamespaceCreateWebsiteSourceArgs
    >(CREATE_WEBSITE_SOURCE_MUTATION, {
      onError: handleGraphQLError,
      onCompleted: onSaved,
    });

    const [updateWebsite, { loading: updating }] = useMutation<
      Pick<Mutation, 'aiAgents'>,
      AiAgentsNamespaceUpdateWebsiteSourceArgs
    >(UPDATE_WEBSITE_SOURCE_MUTATION, {
      onError: handleGraphQLError,
      onCompleted: onSaved,
    });

    const submit = (values: WebsiteFormValues) => {
      const settings = toSettings(values);

      if (target.mode === 'edit') {
        void updateWebsite({
          variables: { input: { companyId, id: target.website.id, ...settings } },
        });

        return;
      }

      void createWebsite({
        variables: { input: { companyId, knowledgeBaseId, ...settings } },
      });
    };

    return (
      <InlineFormModal<WebsiteFormValues>
        testId="website-form"
        title={title}
        visibilityHandler={visibilityHandler}
        form={form}
        onSubmit={submit}
        onCancel={onClose}
        isSubmitting={creating || updating}
        disableSubmitButton={!form.formState.isValid}
        submitButtonText={submitButtonText}
        width="600px"
        hideCancelButton
      >
        <Fields $singlePage={!isMultiPage}>
          <Controller
            control={form.control}
            name="mode"
            render={({ field }) => (
              <ModeSwitch>
                {CRAWL_MODES.map((crawlMode) => (
                  <ModeOption
                    key={crawlMode}
                    type="button"
                    $active={field.value === crawlMode}
                    disabled={isEdit}
                    data-lgg-id={`website-form-mode-${kebabCase(crawlMode)}`}
                    onClick={() => field.onChange(crawlMode)}
                  >
                    {t(`aiAgents:knowledge.websites.form.${MODE_LABEL_KEYS[crawlMode]}`)}
                  </ModeOption>
                ))}
              </ModeSwitch>
            )}
          />
          <Controller
            control={form.control}
            name="url"
            render={({ field, fieldState }) => {
              const urlInput = (
                <TextInput
                  {...field}
                  disabled={isEdit}
                  data-lgg-id="website-form-url"
                  label={
                    isMultiPage ? undefined : t('aiAgents:knowledge.websites.form.url')
                  }
                  placeholder={t('aiAgents:knowledge.websites.form.urlPlaceholder')}
                  error={
                    fieldState.error && field.value
                      ? t('aiAgents:knowledge.websites.form.invalidUrl')
                      : undefined
                  }
                  reserveErrorArea={false}
                  autoComplete="off"
                />
              );

              if (!isMultiPage) {
                return urlInput;
              }

              return (
                <LabeledField>
                  <FieldLabel>{t('aiAgents:knowledge.websites.form.rootUrl')}</FieldLabel>
                  <LockedUrlField>{urlInput}</LockedUrlField>
                </LabeledField>
              );
            }}
          />
          {isMultiPage && (
            <>
              <Controller
                control={form.control}
                name="maxPages"
                render={({ field, fieldState }) => (
                  <LabeledField>
                    <FieldLabel>
                      {t('aiAgents:knowledge.websites.form.maxPages')}
                    </FieldLabel>
                    <SuffixedInput>
                      <TextInput
                        {...field}
                        type="number"
                        min={1}
                        data-lgg-id="website-form-max-pages"
                        error={Boolean(fieldState.error)}
                        showErrorMessage={false}
                        reserveErrorArea={false}
                      />
                      <InputSuffix data-lgg-id="website-form-max-pages-suffix">
                        {t('aiAgents:knowledge.websites.form.pagesSuffix')}
                      </InputSuffix>
                    </SuffixedInput>
                  </LabeledField>
                )}
              />
              <LinkButton
                type="button"
                data-lgg-id="website-form-advanced-toggle"
                onClick={() => setShowAdvanced((visible) => !visible)}
              >
                {t('aiAgents:knowledge.websites.form.advancedSettings')}
                <LinkIcon type="chevronDown" $size={12} $flipped={showAdvanced} />
              </LinkButton>
              {showAdvanced && <AdvancedSettings control={form.control} />}
            </>
          )}
        </Fields>
      </InlineFormModal>
    );
  },
);
